import { contentSlideHeader } from '@features/lci-report/utils';
import { Slide, TextProps } from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { formatKeyTakeawaysText } from '../sales-attribution/key-takeaways-text';
import { dateRangeToString } from '@utils/dates';

const LINE_SPACING = 6;

type MetricTextOptions = {
  name: string;
  value: string;
  description: string;
  textOptions: TextProps['options'];
};

const metricText = (
  options: MetricTextOptions
): NonNullable<Slide['texts']> => [
  {
    text: [
      {
        text: options.name.toLocaleUpperCase(),
        options: {
          bold: true,
          color: unifiedPlatformColorPalette.inmarketGreen,
          fontSize: 20,
          breakLine: true,
        },
      },
      {
        text: options.value,
        options: {
          fontSize: 30,
          breakLine: true,
        },
      },
      {
        text: options.description,
        options: {
          fontSize: 10,
          breakLine: true,
        },
      },
    ],
    options: options.textOptions,
  },
];

type MetricTextPropsOptions = {
  totalVisits: number;
  costPerVisit: number;
};

const metricTextProps = (
  options: MetricTextPropsOptions
): MetricTextOptions[] => [
  {
    name: 'Total Visits',
    value: formatters.visits(options.totalVisits, { full: true }),
    description: 'Projected Visits After Ad Exposure',
    textOptions: {
      x: 0.74,
      y: 1.84,
      w: 2.63,
      h: 1.3,
      paraSpaceAfter: LINE_SPACING,
    },
  },
  {
    name: 'Cost Per Visit',
    value: formatters.currency(options.costPerVisit, { decimals: 2 }),
    description: 'Spend / Total Visits',
    textOptions: {
      x: 0.74,
      y: 3.42,
      w: 2.63,
      h: 1.3,
      paraSpaceAfter: LINE_SPACING,
    },
  },
];

type SecondaryMetricTextOptions = {
  name: string;
  value: string;
  textOptions: TextProps['options'];
};

const secondaryMetricText = (
  options: SecondaryMetricTextOptions
): NonNullable<Slide['texts']> => [
  {
    text: [
      {
        text: options.name.toLocaleUpperCase(),
        options: {
          bold: true,
          color: unifiedPlatformColorPalette.inmarketGreen,
          fontSize: 11,
          breakLine: true,
          paraSpaceAfter: LINE_SPACING,
        },
      },
      {
        text: options.value,
        options: {
          fontSize: 11,
          breakLine: true,
          paraSpaceAfter: LINE_SPACING,
        },
      },
    ],
    options: options.textOptions,
  },
];

type SecondaryMetricTextPropsOptions = {
  measurementStartDate: string;
  measurementEndDate: string;
  locationsMeasured: number;
};

const secondaryMetricTextProps = (
  options: SecondaryMetricTextPropsOptions
): SecondaryMetricTextOptions[] => [
  {
    name: 'Measurement Period',
    value: dateRangeToString([
      options.measurementStartDate,
      options.measurementEndDate,
    ]),
    textOptions: {
      x: 0.74,
      y: 5.05,
      w: 2.63,
      h: 0.54,
    },
  },
  {
    name: 'Locations Measured',
    value: formatters.number(options.locationsMeasured, { full: true }),
    textOptions: {
      x: 0.74,
      y: 5.81,
      w: 2.63,
      h: 0.54,
    },
  },
];

type LciLiteSummaryProps = {
  slideMaster: string;
  totalVisits: number;
  costPerVisit: number;
  measurementStartDate: string;
  measurementEndDate: string;
  locationsMeasured: number;
  keyTakeaways: string[];
};

export const lciLiteSummarySlide = (props: LciLiteSummaryProps): Slide => {
  return {
    slideMaster: props.slideMaster,
    texts: [
      contentSlideHeader('Lite LCI Summary'.toLocaleUpperCase()),
      ...metricTextProps({
        totalVisits: props.totalVisits,
        costPerVisit: props.costPerVisit,
      }).flatMap(options => metricText(options)),
      ...secondaryMetricTextProps({
        measurementStartDate: props.measurementStartDate,
        measurementEndDate: props.measurementEndDate,
        locationsMeasured: props.locationsMeasured,
      }).flatMap(options => secondaryMetricText(options)),
      ...(props.keyTakeaways.length > 0
        ? formatKeyTakeawaysText(
            props.keyTakeaways.join('\n'),
            {
              x: 6.65,
              y: 1.84,
              w: 6.23,
              h: 0.5,
              fontSize: 20,
            },
            {
              x: 6.65,
              y: 2.39,
              w: 6.23,
              h: 3.97,
            }
          )
        : []),
    ],
  };
};
